import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Eraser, PenLine, Type, Undo2 } from "lucide-react";

/**
 * SignaturePad - A comprehensive signature component supporting both drawn and typed signatures
 * 
 * Features:
 * - Draw mode: Touch/mouse/trackpad drawing with HTML5 Canvas
 * - Type mode: Typed signature with cursive styling
 * - Mobile-optimized: Prevents page scroll while drawing
 * - Clear button for easy reset
 * - Responsive: Full-width, 200px height for comfortable signing
 */

/**
 * Safe method to get signature data from canvas.
 * Uses regular canvas.toDataURL() since getTrimmedCanvas() has Webpack import issues
 * with the trim-canvas library in this build.
 */
const getSafeSignatureData = (sigCanvas) => {
  if (!sigCanvas) return null;
  
  try {
    // Use the regular canvas toDataURL directly (includes whitespace but is reliable)
    // Note: getTrimmedCanvas() is avoided because trim-canvas has Webpack import issues
    // that cause "trim_canvas is not a function" errors in production builds.
    const canvas = sigCanvas.getCanvas();
    if (canvas) {
      return canvas.toDataURL("image/png");
    }
  } catch (err) {
    console.error("Failed to export signature canvas:", err);
  }
  
  return null;
};

const SignaturePad = forwardRef(({
  onChange,
  label = "Signature",
  required = false,
  disabled = false,
  initialMode = "draw", // "draw" or "type"
  initialValue = null, // For typed: string, for drawn: base64 string
}, ref) => {
  const [mode, setMode] = useState(initialMode);
  const [typedValue, setTypedValue] = useState(initialValue?.typed || "");
  const [isEmpty, setIsEmpty] = useState(true);
  const sigCanvasRef = useRef(null);
  const containerRef = useRef(null);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    clear: () => {
      if (mode === "draw") {
        sigCanvasRef.current?.clear();
        setIsEmpty(true);
      } else {
        setTypedValue("");
      }
      onChange?.({ type: mode, data: null, isEmpty: true });
    },
    isEmpty: () => {
      if (mode === "draw") {
        return sigCanvasRef.current?.isEmpty() ?? true;
      }
      return !typedValue.trim();
    },
    getSignatureData: () => {
      if (mode === "draw") {
        if (sigCanvasRef.current?.isEmpty()) return null;
        const data = getSafeSignatureData(sigCanvasRef.current);
        if (!data) return null;
        return {
          type: "drawn",
          data,
        };
      } else {
        if (!typedValue.trim()) return null;
        return {
          type: "typed",
          data: typedValue.trim(),
        };
      }
    },
  }));

  // Prevent page scroll when touching the canvas on mobile
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const preventScroll = (e) => {
      if (mode === "draw" && !disabled) {
        e.preventDefault();
      }
    };

    // Use passive: false to allow preventDefault
    container.addEventListener("touchstart", preventScroll, { passive: false });
    container.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      container.removeEventListener("touchstart", preventScroll);
      container.removeEventListener("touchmove", preventScroll);
    };
  }, [mode, disabled]);

  // Handle draw end
  const handleDrawEnd = () => {
    const empty = sigCanvasRef.current?.isEmpty() ?? true;
    setIsEmpty(empty);
    if (!empty) {
      try {
        const data = getSafeSignatureData(sigCanvasRef.current);
        onChange?.({ type: "drawn", data, isEmpty: !data });
      } catch (err) {
        console.error("Error exporting signature:", err);
        onChange?.({ type: "drawn", data: null, isEmpty: true });
      }
    } else {
      onChange?.({ type: "drawn", data: null, isEmpty: true });
    }
  };

  // Handle typed signature change
  const handleTypedChange = (value) => {
    setTypedValue(value);
    onChange?.({
      type: "typed",
      data: value.trim() || null,
      isEmpty: !value.trim(),
    });
  };

  // Clear signature
  const handleClear = () => {
    if (mode === "draw") {
      sigCanvasRef.current?.clear();
      setIsEmpty(true);
      onChange?.({ type: "drawn", data: null, isEmpty: true });
    } else {
      setTypedValue("");
      onChange?.({ type: "typed", data: null, isEmpty: true });
    }
  };

  // Undo the last drawn stroke
  const handleUndo = () => {
    const pad = sigCanvasRef.current;
    if (mode !== "draw" || !pad) return;
    const strokes = pad.toData() || [];
    if (!strokes.length) return;
    strokes.pop();
    pad.fromData(strokes);
    const empty = strokes.length === 0;
    setIsEmpty(empty);
    onChange?.(
      empty
        ? { type: "drawn", data: null, isEmpty: true }
        : { type: "drawn", data: getSafeSignatureData(pad), isEmpty: false }
    );
  };

  // Switch mode
  const switchMode = (newMode) => {
    setMode(newMode);
    // Reset when switching modes
    if (newMode === "draw") {
      sigCanvasRef.current?.clear();
      setIsEmpty(true);
    } else {
      setTypedValue("");
    }
    onChange?.({ type: newMode, data: null, isEmpty: true });
  };

  return (
    <div className="w-full" data-testid="signature-pad-container">
      {/* Label */}
      <label className="text-xs font-medium text-[#1A1B1A] mb-2 block">
        {label} {required && <span className="text-red-400">*</span>}
      </label>

      {/* Mode toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => switchMode("draw")}
          disabled={disabled}
          data-testid="signature-mode-draw"
          className={`flex items-center gap-1.5 px-4 py-2.5 sm:py-1.5 rounded-full text-xs font-medium transition-all ${
            mode === "draw"
              ? "bg-[#205B5A] text-white"
              : "bg-[#FAF9F6] text-[#757675] hover:bg-[#F3F2EE]"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <PenLine className="w-3 h-3" />
          Draw signature
        </button>
        <button
          type="button"
          onClick={() => switchMode("type")}
          disabled={disabled}
          data-testid="signature-mode-type"
          className={`flex items-center gap-1.5 px-4 py-2.5 sm:py-1.5 rounded-full text-xs font-medium transition-all ${
            mode === "type"
              ? "bg-[#205B5A] text-white"
              : "bg-[#FAF9F6] text-[#757675] hover:bg-[#F3F2EE]"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <Type className="w-3 h-3" />
          Type instead
        </button>
      </div>

      {/* Signature area */}
      <div
        ref={containerRef}
        className={`relative border rounded-xl overflow-hidden ${
          disabled ? "bg-[#FAF9F6] opacity-60" : "bg-white"
        } ${mode === "draw" ? "border-[#1A1B1A]/15 border-dashed" : "border-[#1A1B1A]/10"}`}
        style={{ touchAction: mode === "draw" ? "none" : "auto" }}
      >
        {mode === "draw" ? (
          <>
            <SignatureCanvas
              ref={sigCanvasRef}
              canvasProps={{
                className: "w-full",
                style: { 
                  width: "100%", 
                  height: "200px",
                  cursor: disabled ? "not-allowed" : "crosshair",
                },
                "data-testid": "signature-canvas",
              }}
              penColor="#1A1B1A"
              minWidth={1.5}
              maxWidth={3}
              velocityFilterWeight={0.7}
              onEnd={handleDrawEnd}
            />
            {/* Placeholder text when empty */}
            {isEmpty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-sm text-[#757675]/50">
                  {disabled ? "Signature disabled" : "Sign here with your finger, mouse, or trackpad"}
                </p>
              </div>
            )}
            {/* Signature line */}
            <div className="absolute bottom-8 left-6 right-6 border-b border-[#1A1B1A]/10" />
            <div className="absolute bottom-3 left-6">
              <span className="text-[9px] text-[#757675]">×</span>
            </div>
          </>
        ) : (
          <div className="p-4" style={{ minHeight: "200px" }}>
            <input
              type="text"
              value={typedValue}
              onChange={(e) => handleTypedChange(e.target.value)}
              placeholder="Type your full legal name"
              disabled={disabled}
              data-testid="signature-typed-input"
              className="w-full text-2xl text-[#1A1B1A] bg-transparent border-none outline-none italic"
              style={{ 
                fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive",
                letterSpacing: "1px",
              }}
            />
            <div className="mt-4 border-b border-[#1A1B1A]/10" />
            <p className="text-[10px] text-[#757675] mt-2">
              By typing your name above, you are providing your electronic signature.
            </p>
          </div>
        )}

        {/* No tiny hidden icon - the visible Clear signature button sits below the pad */}
      </div>

      {/* Clear / Undo - always visible, finger friendly */}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button
            type="button"
            onClick={handleClear}
            data-testid="signature-clear-btn"
            className="flex items-center justify-center gap-2 px-5 py-3 sm:py-2 min-h-[44px] sm:min-h-0 rounded-xl border border-[#1A1B1A]/15 bg-white text-sm sm:text-xs font-medium text-[#1A1B1A] hover:bg-[#FAF9F6] active:scale-[0.98] transition-all"
          >
            <Eraser className="w-4 h-4" />
            Clear signature
          </button>
          {mode === "draw" && (
            <button
              type="button"
              onClick={handleUndo}
              disabled={isEmpty}
              data-testid="signature-undo-btn"
              className={`flex items-center justify-center gap-2 px-4 py-3 sm:py-2 min-h-[44px] sm:min-h-0 rounded-xl text-sm sm:text-xs font-medium transition-all ${
                isEmpty
                  ? "text-[#9A9B9A] cursor-not-allowed"
                  : "text-[#4A4B4A] hover:bg-[#FAF9F6] active:scale-[0.98]"
              }`}
            >
              <Undo2 className="w-4 h-4" />
              Undo stroke
            </button>
          )}
        </div>
      )}

      {/* Help text */}
      <p className="text-[10px] text-[#757675] mt-2">
        {mode === "draw" 
          ? "Draw your signature using your finger, mouse, or trackpad. Tap Clear signature to start again."
          : "Type your full legal name as your electronic signature."
        }
      </p>
    </div>
  );
});

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;
